import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

/**
 * O tema do jogo: claro, escuro ou o do brasão do clube.
 *
 * Claro e escuro já existiam em `tokens.css` e nunca tinham sido ligados: o
 * tema claro estava escrito por inteiro e nada no jogo definia `data-theme`.
 *
 * O do brasão é o que precisa de conta. Ele não pinta a tela com as cores do
 * clube, ele **empresta a cor da marca**: o fundo escuro fica, com um tom
 * puxado para a cor do clube, e o que era verde do Legacy vira a cor dele. Sem
 * isso, um clube de camisa branca ou amarela deixaria o texto ilegível, e um
 * de camisa preta apagaria a tela inteira.
 *
 * **Por isso a cor escolhida passa por uma faixa de luminosidade.** A cor do
 * clube entra como matiz e saturação; a luminosidade é forçada para uma faixa
 * que se lê sobre fundo escuro. O amarelo continua amarelo e o azul continua
 * azul, mas nenhum dos dois consegue ficar claro demais ou escuro demais para
 * o lugar onde vai.
 */

export type Tema = "escuro" | "claro" | "brasao";

export const TEMAS: readonly Tema[] = ["escuro", "claro", "brasao"];

const CHAVE = "lm:tema";

/** As cores que o tema do brasão usa, quando há clube aberto. */
export interface CoresDoClube {
  primary: string;
  secondary: string;
  detail: string;
}

/*
 * Leitura e escrita em try/catch, como na preferência de idioma: janela
 * anônima, dados do site bloqueados e WebView com armazenamento desligado
 * jogam nas duas pontas. O jogo continua inteiro sem isso, só esquece a
 * escolha ao fechar.
 */

function temaGuardado(): Tema | null {
  try {
    const bruto = localStorage.getItem(CHAVE);
    return TEMAS.includes(bruto as Tema) ? (bruto as Tema) : null;
  } catch {
    return null;
  }
}

function guardarTema(tema: Tema): void {
  try {
    localStorage.setItem(CHAVE, tema);
  } catch {
    // Sem lugar para guardar. A escolha vale até fechar a aba, e pronto.
  }
}

// ---------------------------------------------------------------------------
// Cor
// ---------------------------------------------------------------------------

interface Hsl {
  h: number;
  s: number;
  l: number;
}

/** Aceita `#abc` e `#aabbcc`, e devolve `null` para qualquer outra coisa. */
function paraHsl(hex: string): Hsl | null {
  const limpo = hex.trim().replace("#", "");
  const cheio =
    limpo.length === 3
      ? limpo
          .split("")
          .map((letra) => letra + letra)
          .join("")
      : limpo;
  if (!/^[0-9a-fA-F]{6}$/.test(cheio)) return null;

  const r = Number.parseInt(cheio.slice(0, 2), 16) / 255;
  const g = Number.parseInt(cheio.slice(2, 4), 16) / 255;
  const b = Number.parseInt(cheio.slice(4, 6), 16) / 255;

  const maior = Math.max(r, g, b);
  const menor = Math.min(r, g, b);
  const l = (maior + menor) / 2;
  const d = maior - menor;

  if (d === 0) return { h: 0, s: 0, l: l * 100 };

  const s = d / (1 - Math.abs(2 * l - 1));
  let h: number;
  if (maior === r) h = ((g - b) / d) % 6;
  else if (maior === g) h = (b - r) / d + 2;
  else h = (r - g) / d + 4;

  return { h: ((h * 60) % 360 + 360) % 360, s: s * 100, l: l * 100 };
}

function css({ h, s, l }: Hsl): string {
  return `hsl(${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%)`;
}

const limitar = (valor: number, minimo: number, maximo: number): number =>
  Math.min(maximo, Math.max(minimo, valor));

/**
 * A cor do clube que serve de marca, e nunca a que some.
 *
 * Um clube de preto e branco tem as duas cores sem saturação nenhuma, e usar
 * qualquer uma delas daria um tema cinza morto. Nesse caso vale o verde de
 * sempre. Entre as três do escudo, ganha a mais saturada, que é a que a pessoa
 * reconhece como sendo a cor do time.
 */
function corDeMarca(cores: CoresDoClube): Hsl | null {
  const candidatas = [cores.primary, cores.detail, cores.secondary]
    .map(paraHsl)
    .filter((cor): cor is Hsl => cor !== null)
    .filter((cor) => cor.s >= 25);

  if (candidatas.length === 0) return null;
  return candidatas.reduce((melhor, cor) => (cor.s > melhor.s ? cor : melhor));
}

/**
 * As variáveis que o tema do brasão sobrescreve.
 *
 * Só as de marca e as de superfície. Perigo, aviso e sucesso continuam
 * vermelho, laranja e verde em qualquer tema, porque num jogo de futebol elas
 * são leitura e não enfeite: moral, forma e condição se leem pela cor, e
 * repintá-las com a cor do clube apagaria o que elas dizem.
 */
function variaveisDoBrasao(cores: CoresDoClube): Record<string, string> | null {
  const marca = corDeMarca(cores);
  if (!marca) return null;

  const { h } = marca;
  const s = limitar(marca.s, 45, 85);

  /*
   * A luminosidade é forçada, e é o que impede o tema de ficar ilegível.
   *
   * A cor vem do clube, mas onde ela vai é decisão daqui: 55% se lê sobre o
   * fundo escuro sem estourar, e o fundo fica entre 4% e 12%, que é escuro o
   * bastante para o texto claro continuar valendo.
   */
  const brilho = 55;
  const tinta = brilho > 62 ? "hsl(0 0% 8%)" : "hsl(0 0% 100%)";

  return {
    "--bg": css({ h, s: Math.min(s, 22), l: 4 }),
    "--bg-deep": css({ h, s: Math.min(s, 22), l: 3 }),
    "--surface": css({ h, s: Math.min(s, 20), l: 7 }),
    "--surface-2": css({ h, s: Math.min(s, 20), l: 10 }),
    "--surface-3": css({ h, s: Math.min(s, 20), l: 13 }),
    "--border": css({ h, s: Math.min(s, 24), l: 17 }),
    "--border-strong": css({ h, s: Math.min(s, 24), l: 25 }),
    "--primary": css({ h, s, l: brilho }),
    "--primary-dim": css({ h, s, l: brilho - 16 }),
    "--primary-ink": tinta,
    "--primary-glow": `hsl(${Math.round(h)} ${Math.round(s)}% ${brilho}% / 0.18)`,
  };
}

// ---------------------------------------------------------------------------
// O provedor
// ---------------------------------------------------------------------------

interface Balcao {
  tema: Tema;
  definirTema: (tema: Tema) => void;
  /**
   * As cores do clube aberto, ou `null`.
   *
   * Quem manda isto é quem tem a visão do clube na mão. O tema do brasão sem
   * clube nenhum cai no escuro de sempre, que é o caso de quem ainda não
   * começou carreira.
   */
  definirCoresDoClube: (cores: CoresDoClube | null) => void;
  /** Se o tema do brasão tem de onde tirar cor agora. */
  brasaoDisponivel: boolean;
}

const Contexto = createContext<Balcao | null>(null);

export function useTema(): Balcao {
  const balcao = useContext(Contexto);
  if (!balcao) throw new Error("useTema precisa estar dentro de ProvedorDeTema");
  return balcao;
}

export function ProvedorDeTema({ children }: { children: ReactNode }): ReactNode {
  const [tema, definirTemaCru] = useState<Tema>(() => temaGuardado() ?? "escuro");
  const [cores, definirCoresDoClube] = useState<CoresDoClube | null>(null);

  const definirTema = useCallback((novo: Tema) => {
    definirTemaCru(novo);
    guardarTema(novo);
  }, []);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const raiz = document.documentElement;
    const doBrasao = tema === "brasao" ? (cores ? variaveisDoBrasao(cores) : null) : null;

    /*
     * O tema do brasão veste o escuro por baixo.
     *
     * Ele sobrescreve marca e superfície, e deixa texto, perigo, aviso e
     * sucesso como estão. Sem a base escura embaixo, cada variável que ele não
     * define ficaria valendo a do tema anterior.
     */
    raiz.dataset["theme"] = tema === "claro" ? "light" : "dark";

    /* O que não vale mais sai antes, senão o tema de um clube fica no outro. */
    for (const nome of VARIAVEIS) raiz.style.removeProperty(nome);
    if (doBrasao) {
      for (const [nome, valor] of Object.entries(doBrasao)) raiz.style.setProperty(nome, valor);
    }
  }, [tema, cores]);

  const balcao = useMemo(
    () => ({
      tema,
      definirTema,
      definirCoresDoClube,
      brasaoDisponivel: cores !== null && variaveisDoBrasao(cores) !== null,
    }),
    [tema, definirTema, cores],
  );

  return <Contexto.Provider value={balcao}>{children}</Contexto.Provider>;
}

/** Tudo o que o tema do brasão pode escrever, para poder apagar depois. */
const VARIAVEIS = [
  "--bg",
  "--bg-deep",
  "--surface",
  "--surface-2",
  "--surface-3",
  "--border",
  "--border-strong",
  "--primary",
  "--primary-dim",
  "--primary-ink",
  "--primary-glow",
] as const;
